import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Eye, 
  EyeOff,
  Upload,
  AlertTriangle,
  Check,
  X,
  Loader2,
  Image as ImageIcon,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Image requirements
const IMAGE_REQUIREMENTS = {
  formats: ['image/jpeg', 'image/png', 'image/jpg'],
  recommendedWidth: 1920,
  recommendedHeight: 900,
  maxSizeKB: 500,
  aspectRatio: 1920 / 900 // ~2.13
};

export const AdminCarousel = () => {
  const { isAdmin, authFetch } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  const fetchImages = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/carousel`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
      toast.error('Error carregant imatges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleInitCarousel = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/carousel/init`, {
        method: 'POST',
        
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchImages();
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error('Error inicialitzant carrusel');
    }
  };

  const handleToggleActive = async (imageId, currentState) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/carousel/${imageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ is_active: !currentState })
      });
      if (response.ok) {
        setImages(prev => prev.map(img => 
          img.id === imageId ? { ...img, is_active: !currentState } : img
        ));
        toast.success(currentState ? 'Imatge desactivada' : 'Imatge activada');
      }
    } catch (error) {
      toast.error('Error actualitzant imatge');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Segur que vols eliminar aquesta imatge del carrusel?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/carousel/${imageId}`, {
        method: 'DELETE',
        
      });
      if (response.ok) {
        setImages(prev => prev.filter(img => img.id !== imageId));
        toast.success('Imatge eliminada');
      }
    } catch (error) {
      toast.error('Error eliminant imatge');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newImages = [...images];
    const draggedImage = newImages[draggedItem];
    newImages.splice(draggedItem, 1);
    newImages.splice(index, 0, draggedImage);
    
    setImages(newImages);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;
    setDraggedItem(null);
    
    // Save new order to backend
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/carousel/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ image_ids: images.map(img => img.id) })
      });
      if (response.ok) {
        toast.success('Ordre guardat');
      }
    } catch (error) {
      toast.error('Error guardant ordre');
      fetchImages(); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleImageAdded = (newImage) => {
    setImages(prev => [...prev, newImage]);
    setShowUploadModal(false);
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#333333]">Accés restringit</h2>
          <p className="text-[#666666] mt-2">Només els administradors poden gestionar el carrusel.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-carousel">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E]">
              Carrusel de la HOME
            </h1>
            <p className="text-[#666666] mt-1">
              Gestiona les imatges del carrusel principal
            </p>
          </div>
          <div className="flex gap-2">
            {images.length === 0 && (
              <Button
                variant="outline"
                onClick={handleInitCarousel}
                data-testid="init-carousel-btn"
              >
                Inicialitzar amb imatges actuals
              </Button>
            )}
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
              data-testid="add-image-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Afegir imatge
            </Button>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Instruccions:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Arrossega les imatges per reordenar-les</li>
                <li>Utilitza l'ull per activar/desactivar sense eliminar</li>
                <li>Les imatges desactivades no es mostren a la web</li>
                <li>Format recomanat: 1920×900 px, JPG/PNG, màx 500KB</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Images list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
          </div>
        ) : images.length === 0 ? (
          <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-12 text-center">
            <ImageIcon className="w-16 h-16 text-[#CCCCCC] mx-auto mb-4" />
            <p className="text-[#666666]">No hi ha imatges al carrusel</p>
            <p className="text-sm text-[#888888] mt-2">
              Clica "Inicialitzar" per carregar les imatges actuals o "Afegir imatge" per començar de zero
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {images.map((image, index) => (
              <div
                key={image.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`
                  bg-white border rounded-lg overflow-hidden
                  transition-all duration-200
                  ${draggedItem === index ? 'border-[#1A4D2E] shadow-lg scale-[1.02]' : 'border-[#E5E5E5]'}
                  ${!image.is_active ? 'opacity-60' : ''}
                `}
                data-testid={`carousel-image-${image.id}`}
              >
                <div className="flex items-center gap-4 p-4">
                  {/* Drag handle */}
                  <div className="cursor-grab active:cursor-grabbing text-[#AAAAAA] hover:text-[#666666]">
                    <GripVertical className="w-5 h-5" />
                  </div>

                  {/* Image preview */}
                  <div className="w-32 h-16 bg-[#F5F5F5] rounded overflow-hidden flex-shrink-0">
                    <img
                      src={image.url}
                      alt={image.alt || 'Carousel image'}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Image info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#333333] truncate">
                      {image.alt || image.filename || `Imatge ${index + 1}`}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[#888888] mt-1">
                      <span>Posició: {index + 1}</span>
                      {image.width && image.height && (
                        <span>{image.width}×{image.height}</span>
                      )}
                      {image.size_kb > 0 && (
                        <span>{Math.round(image.size_kb)} KB</span>
                      )}
                    </div>
                  </div>

                  {/* Status badge */}
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    image.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {image.is_active ? 'Activa' : 'Inactiva'}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(image.id, image.is_active)}
                      title={image.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {image.is_active ? (
                        <Eye className="w-4 h-4 text-[#1A4D2E]" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-[#888888]" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(image.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {saving && (
          <div className="fixed bottom-4 right-4 bg-[#1A4D2E] text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Guardant ordre...
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onImageAdded={handleImageAdded}
        />
      )}
    </AdminLayout>
  );
};

// Upload Modal Component
const UploadModal = ({ onClose, onImageAdded }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageInfo, setImageInfo] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);

  const validateImage = useCallback(async (url) => {
    if (!url) {
      setPreviewUrl('');
      setImageInfo(null);
      setWarnings([]);
      return;
    }

    setValidating(true);
    setWarnings([]);

    try {
      // Create an image element to check dimensions
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });

      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const aspectRatio = width / height;
      
      const newWarnings = [];

      // Check dimensions
      if (width < IMAGE_REQUIREMENTS.recommendedWidth || height < IMAGE_REQUIREMENTS.recommendedHeight) {
        newWarnings.push(`Mida menor a la recomanada (${IMAGE_REQUIREMENTS.recommendedWidth}×${IMAGE_REQUIREMENTS.recommendedHeight}px)`);
      }

      // Check aspect ratio (allow 10% tolerance)
      const expectedRatio = IMAGE_REQUIREMENTS.aspectRatio;
      if (aspectRatio < expectedRatio * 0.9 || aspectRatio > expectedRatio * 1.1) {
        newWarnings.push(`Proporció diferent a 1920:900. La imatge es retallarà automàticament.`);
      }

      // Check if vertical
      if (height > width) {
        newWarnings.push('La imatge és vertical. Es recomana orientació horitzontal.');
      }

      setPreviewUrl(url);
      setImageInfo({ width, height, aspectRatio });
      setWarnings(newWarnings);

    } catch (error) {
      setWarnings(['No s\'ha pogut carregar la imatge. Verifica la URL.']);
      setPreviewUrl('');
      setImageInfo(null);
    } finally {
      setValidating(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (imageUrl) {
        validateImage(imageUrl);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [imageUrl, validateImage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageUrl || !previewUrl) return;

    setUploading(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/carousel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          url: imageUrl,
          alt: imageAlt || 'UGOLF ARAVELL Andorra',
          width: imageInfo?.width || 0,
          height: imageInfo?.height || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Imatge afegida al carrusel');
        onImageAdded({
          id: data.image_id,
          url: imageUrl,
          alt: imageAlt,
          is_active: true,
          width: imageInfo?.width || 0,
          height: imageInfo?.height || 0
        });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error afegint imatge');
      }
    } catch (error) {
      toast.error('Error afegint imatge');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#333333]">Afegir imatge al carrusel</h2>
            <button onClick={onClose} className="text-[#888888] hover:text-[#333333]">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Requirements info */}
          <div className="bg-[#F9F9F7] border border-[#E5E5E5] rounded-lg p-4 mb-6">
            <h3 className="font-medium text-[#333333] mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Requisits de la imatge
            </h3>
            <ul className="text-sm text-[#666666] space-y-1">
              <li>• Format recomanat: <strong>JPG / PNG</strong></li>
              <li>• Mida recomanada: <strong>1920 × 900 px</strong></li>
              <li>• Pes màxim recomanat: <strong>≤ 500 KB</strong></li>
              <li>• Orientació: <strong>Horitzontal</strong></li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>URL de la imatge *</Label>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                required
                data-testid="image-url-input"
              />
              <p className="text-xs text-[#888888]">
                Puja la imatge a un servei com Imgur, Cloudinary o similar i enganxa la URL aquí
              </p>
            </div>

            <div className="space-y-2">
              <Label>Text alternatiu (alt)</Label>
              <Input
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                placeholder="Descripció de la imatge per accessibilitat"
              />
            </div>

            {/* Preview */}
            {validating && (
              <div className="flex items-center gap-2 text-[#888888]">
                <Loader2 className="w-4 h-4 animate-spin" />
                Validant imatge...
              </div>
            )}

            {previewUrl && (
              <div className="space-y-2">
                <Label>Previsualització</Label>
                <div className="relative aspect-[1920/900] bg-[#F5F5F5] rounded-lg overflow-hidden">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                {imageInfo && (
                  <p className="text-xs text-[#888888]">
                    Dimensions: {imageInfo.width}×{imageInfo.height}px
                  </p>
                )}
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Avisos:</p>
                    <ul className="text-sm text-amber-700 mt-1 space-y-1">
                      {warnings.map((warning, i) => (
                        <li key={i}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel·lar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#1A4D2E] hover:bg-[#153d25] text-white"
                disabled={!previewUrl || uploading}
                data-testid="submit-image-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Afegint...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Afegir imatge
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminCarousel;
